import { memo } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import {
  CardTitle,
  ChartLoadingState,
  ChartTooltipBox,
  EmptyChartState,
  OVERVIEW_CARD_SX,
} from './OverviewCard';

// Borrowed from the unmatched files last-seen ranges so the distribution charts read as part of
// the same family.
export const DISTRIBUTION_BAR_COLOR = '#87C8E0';

export const DISTRIBUTION_PAGE_SIZE = 5;

const AXIS_ROOM = 32;
const MAX_LABEL_LINES = 2;

const SIZES = {
  regular: { rowHeight: 46, barSize: 20, labelWidth: 260, fontSize: 12, lineHeight: 13 },
  dense: { rowHeight: 30, barSize: 12, labelWidth: 220, fontSize: 10, lineHeight: 11 },
};

const FONT_FAMILY = 'Inter, sans-serif';

let measureContext = null;
const measuredWidths = new Map();

// Wrapping by character count guessed at the width and clipped wide labels, so the text is
// measured against the gutter it actually has.
const measureText = (text, fontSize) => {
  const key = `${fontSize}|${text}`;
  const cached = measuredWidths.get(key);
  if (cached !== undefined) {
    return cached;
  }

  if (!measureContext) {
    measureContext = document.createElement('canvas').getContext('2d');
  }
  measureContext.font = `${fontSize}px ${FONT_FAMILY}`;
  const width = measureContext.measureText(text).width;
  measuredWidths.set(key, width);
  return width;
};

const longestPrefixWithin = (text, maxWidth, fontSize) => {
  let end = text.length;
  while (end > 1 && measureText(text.slice(0, end), fontSize) > maxWidth) {
    end -= 1;
  }
  return end;
};

const truncateToWidth = (text, maxWidth, fontSize) => {
  if (measureText(text, fontSize) <= maxWidth) {
    return text;
  }

  const end = longestPrefixWithin(`${text}…`, maxWidth, fontSize);
  return `${text.slice(0, Math.max(1, end - 1)).trimEnd()}…`;
};

// Policy and binary names are often long, so a label wraps onto a second line and only then gets
// an ellipsis. Word boundaries are preferred, but names with no spaces still break cleanly.
const wrapLabel = (text, maxWidth, fontSize) => {
  const lines = [];
  let remaining = text.trim();

  while (remaining && lines.length < MAX_LABEL_LINES) {
    if (measureText(remaining, fontSize) <= maxWidth) {
      lines.push(remaining);
      break;
    }

    if (lines.length === MAX_LABEL_LINES - 1) {
      lines.push(truncateToWidth(remaining, maxWidth, fontSize));
      break;
    }

    const end = longestPrefixWithin(remaining, maxWidth, fontSize);
    const wordBreak = remaining.lastIndexOf(' ', end);
    const cut = wordBreak > 0 ? wordBreak : end;
    lines.push(remaining.slice(0, cut).trimEnd());
    remaining = remaining.slice(cut).trimStart();
  }

  return lines;
};

// The axis is keyed on id rather than label so two categories that happen to share a name still
// get their own bar; the label is read back out of the row the tick belongs to.
const renderCategoryTick = ({ x, y, payload }, data, size) => {
  const label = String(data[payload.index]?.label ?? '');
  const lines = wrapLabel(label, size.labelWidth - 12, size.fontSize);
  const firstLineOffset = size.fontSize / 3 - ((lines.length - 1) * size.lineHeight) / 2;

  return (
    <g transform={`translate(${x},${y})`}>
      <text
        textAnchor="end"
        style={{ fontSize: size.fontSize, fill: '#E5E7EB', pointerEvents: 'none' }}>
        <title>{label}</title>
        {lines.map((line, index) => (
          <tspan
            key={`${index}-${line}`}
            x={0}
            dy={index === 0 ? firstLineOffset : size.lineHeight}>
            {line}
          </tspan>
        ))}
      </text>
    </g>
  );
};

const renderTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) {
    return null;
  }

  const { label, value, deviceCount } = payload[0].payload;

  return (
    <ChartTooltipBox>
      <Box sx={{ fontWeight: 700 }}>{label}</Box>
      <Box>
        {value}%
        {deviceCount == null ? '' : ` · ${deviceCount} device${deviceCount === 1 ? '' : 's'}`}
      </Box>
    </ChartTooltipBox>
  );
};

const DistributionBarChart = ({
  title,
  subtitle,
  data = [],
  total,
  isLoading,
  isLoadingMore,
  hasMore,
  onLoadMore,
  onItemClick,
  dense = false,
  titleSize = 'md',
  emptyMessage = 'No data yet',
  sx,
}) => {
  const size = dense ? SIZES.dense : SIZES.regular;
  const chartHeight = data.length * size.rowHeight + AXIS_ROOM;

  return (
    <Stack gap={dense ? 0.75 : 1} sx={{ ...OVERVIEW_CARD_SX, minWidth: 0, ...sx }}>
      <CardTitle title={title} subtitle={subtitle} size={titleSize} />

      {isLoading ? (
        <ChartLoadingState height={dense ? 120 : 200} />
      ) : !data.length ? (
        <EmptyChartState height={dense ? 120 : 200} message={emptyMessage} />
      ) : (
        <>
          <Box sx={{ width: '100%', height: chartHeight }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                layout="vertical"
                data={data}
                margin={{ top: 4, right: dense ? 34 : 44, bottom: 0, left: 0 }}>
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="rgba(255,255,255,0.16)"
                  horizontal={false}
                />

                <XAxis
                  type="number"
                  domain={[0, 100]}
                  tickFormatter={v => `${v}%`}
                  stroke="#D1D5DB"
                  tickLine={false}
                  axisLine={false}
                  tick={{ fontSize: dense ? 9 : 10, fill: '#D1D5DB' }}
                />

                <YAxis
                  type="category"
                  dataKey="id"
                  width={size.labelWidth}
                  tickLine={false}
                  axisLine={false}
                  interval={0}
                  tick={props => renderCategoryTick(props, data, size)}
                />

                <Tooltip
                  isAnimationActive={false}
                  cursor={{ fill: 'rgba(255,255,255,0.06)' }}
                  wrapperStyle={{ whiteSpace: 'normal' }}
                  content={renderTooltip}
                />

                <Bar
                  dataKey="value"
                  fill={DISTRIBUTION_BAR_COLOR}
                  barSize={size.barSize}
                  minPointSize={2}
                  radius={[0, 4, 4, 0]}
                  cursor={onItemClick ? 'pointer' : 'default'}
                  onClick={entry => onItemClick?.(entry.payload)}>
                  <LabelList
                    dataKey="value"
                    position="right"
                    formatter={v => `${v}%`}
                    style={{ fontSize: dense ? 9 : 10, fill: '#D1D5DB' }}
                  />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Box>

          <Stack direction="row" gap={1} alignItems="center" justifyContent="center">
            <Typography sx={{ color: '#9CA3AF', fontSize: dense ? '0.62rem' : '0.68rem' }}>
              Showing {data.length} of {total ?? data.length}
            </Typography>

            {hasMore ? (
              <Button
                onClick={onLoadMore}
                disabled={isLoadingMore}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: dense ? '0.62rem' : '0.68rem',
                  color: '#A9C9D6',
                  backgroundColor: 'rgba(135, 200, 224, 0.12)',
                  border: '1px solid rgba(135, 200, 224, 0.28)',
                  borderRadius: '6px',
                  px: 1,
                  py: 0.25,
                  minWidth: 0,
                  '&:hover': { backgroundColor: 'rgba(135, 200, 224, 0.24)', color: '#D7EAF2' },
                }}>
                {isLoadingMore ? 'Loading…' : 'Load more'}
              </Button>
            ) : null}
          </Stack>
        </>
      )}
    </Stack>
  );
};

export default memo(DistributionBarChart);
